'use client'

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { toast } from 'react-hot-toast'
import Cookies from 'js-cookie'

// Profile type definition
export interface Profile {
  profile_id: number
  name: string
  description?: string
  created_at: string
  deleted_at?: string | null
}

// Context type definition
interface ProfileContextType {
  activeProfile: Profile | null
  profiles: Profile[]
  switchProfile: (profileId: number) => void
  refreshProfiles: () => Promise<void>
  isLoading: boolean
}

// Create context with undefined default
const ProfileContext = createContext<ProfileContextType | undefined>(undefined)

// Custom hook to use the context
export function useProfile() {
  const context = useContext(ProfileContext)
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider')
  }
  return context
}

// Provider component
export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const [activeProfile, setActiveProfile] = useState<Profile | null>(null)
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Get API base URL
  // CLAUDE.md compliant: No fallbacks - fail fast if parameter missing
  const getApiBaseUrl = useCallback(async (): Promise<string> => {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL

    if (!apiUrl) {
      throw new Error('CRITICAL: NEXT_PUBLIC_API_URL environment variable is required but not set')
    }

    return apiUrl
  }, [])

  // Fetch all profiles from API
  const fetchProfiles = useCallback(async (): Promise<Profile[]> => {
    try {
      const apiBase = await getApiBaseUrl()
      const response = await fetch(`${apiBase}/profiles`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch profiles: ${response.status}`)
      }

      const data = await response.json()
      // Map API response (id) to frontend expectation (profile_id)
      // Filter out deleted profiles and sort alphabetically (case-insensitive)
      const activeProfiles = data
        .filter((p: any) => !p.deleted_at)
        .map((p: any) => ({
          profile_id: p.id,
          name: p.name,
          description: p.description,
          created_at: p.created_at,
          deleted_at: p.deleted_at
        }))
        .sort((a: Profile, b: Profile) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

      return activeProfiles
    } catch (error) {
      console.error('Error fetching profiles:', error)
      throw error
    }
  }, [getApiBaseUrl])

  // Refresh profiles (public API for components)
  const refreshProfiles = useCallback(async () => {
    try {
      const fetchedProfiles = await fetchProfiles()
      setProfiles(fetchedProfiles)
    } catch (error) {
      toast.error('Failed to load profiles. Using cached data.')
      // Keep existing profiles in state as fallback
    }
  }, [fetchProfiles])

  // Initialize profile on mount
  useEffect(() => {
    const initializeProfile = async () => {
      try {
        // Fetch all profiles first
        const fetchedProfiles = await fetchProfiles()
        setProfiles(fetchedProfiles)

        // Try to get profile ID from cookie first (SSR compatibility)
        let profileId = parseInt(Cookies.get('profile_id') || '1', 10)

        // Fallback to localStorage if cookie not present
        if (!profileId || profileId === 1) {
          const storedId = localStorage.getItem('activeProfileId')
          if (storedId) {
            profileId = parseInt(storedId, 10)
          }
        }

        // Validate profile ID exists in fetched profiles
        const validProfile = fetchedProfiles.find(p => p.profile_id === profileId)

        if (validProfile) {
          setActiveProfile(validProfile)
          // Sync cookie and localStorage
          Cookies.set('profile_id', profileId.toString(), { expires: 365 })
          localStorage.setItem('activeProfileId', profileId.toString())
        } else {
          // Invalid profile ID - fallback to default (profile_id=1)
          const defaultProfile = fetchedProfiles.find(p => p.profile_id === 1)
          if (defaultProfile) {
            setActiveProfile(defaultProfile)
            Cookies.set('profile_id', '1', { expires: 365 })
            localStorage.setItem('activeProfileId', '1')
          } else {
            // If no default profile exists, use first available
            if (fetchedProfiles.length > 0) {
              setActiveProfile(fetchedProfiles[0])
              Cookies.set('profile_id', fetchedProfiles[0].profile_id.toString(), { expires: 365 })
              localStorage.setItem('activeProfileId', fetchedProfiles[0].profile_id.toString())
            }
          }
        }
      } catch (error) {
        console.error('Failed to initialize profile:', error)
        toast.error('Failed to load profiles. Please refresh the page.')

        // Try to use cached profiles from localStorage as last resort
        const cachedProfilesStr = localStorage.getItem('cachedProfiles')
        if (cachedProfilesStr) {
          try {
            const cachedProfiles = JSON.parse(cachedProfilesStr)
            setProfiles(cachedProfiles)
            const storedId = parseInt(localStorage.getItem('activeProfileId') || '1', 10)
            const cachedActive = cachedProfiles.find((p: Profile) => p.profile_id === storedId)
            if (cachedActive) {
              setActiveProfile(cachedActive)
            }
          } catch (e) {
            console.error('Failed to parse cached profiles:', e)
          }
        }
      } finally {
        setIsLoading(false)
      }
    }

    // Only run on client side
    if (typeof window !== 'undefined') {
      initializeProfile()
    }
  }, [fetchProfiles])

  // Cache profiles to localStorage whenever they change
  useEffect(() => {
    if (profiles.length > 0 && typeof window !== 'undefined') {
      localStorage.setItem('cachedProfiles', JSON.stringify(profiles))
    }
  }, [profiles])

  // Switch to a different profile
  const switchProfile = useCallback((profileId: number) => {
    const profile = profiles.find(p => p.profile_id === profileId)

    if (!profile) {
      toast.error('Profile not found')
      return
    }

    // Save to both localStorage and cookie
    localStorage.setItem('activeProfileId', profileId.toString())
    Cookies.set('profile_id', profileId.toString(), { expires: 365 })

    // Show confirmation
    toast.success(`Switched to profile: ${profile.name}`)

    // Reload page to apply profile change across all components
    // This ensures SSR and all API calls use the new profile
    window.location.reload()
  }, [profiles])

  const contextValue: ProfileContextType = {
    activeProfile,
    profiles,
    switchProfile,
    refreshProfiles,
    isLoading
  }

  return (
    <ProfileContext.Provider value={contextValue}>
      {children}
    </ProfileContext.Provider>
  )
}
